import { useCallback } from 'react';
import { signIn, signOut, refreshAuth, patchPassword } from '@api/auth';
import { LoginReq, LoginRes, UpdateMeReqBody } from '@models/User';
import axios, { AxiosResponse } from 'axios';
import { useAuthPage } from '@features/auth/useAuthPage';
import { useMutation } from '@tanstack/react-query';
import { PATH } from 'config/index';
const useAuth = () => {
  const {
    accessToken,
    isElectron,
    updateAuthState,
    resetAuthState,
    refreshToken,
    tokenExpires,
  } = useAuthPage();

  const storeTokens = useCallback(
    async (data: LoginRes) => {
      updateAuthState(data);
      localStorage.setItem('exp', String(data.tokenExpires));

      if (isElectron) {
        await window.electron.ipcRenderer.storeRefreshToken(data.refreshToken);
      }
    },
    [updateAuthState],
  );

  const login = async ({ userName, password }: LoginReq) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error('Network is offline. Please check your connection.');
    }

    try {
      const response = (await signIn({
        userName,
        password,
      })) as AxiosResponse<LoginRes>;

      if (response.status === 200 || response.status === 201) {
        if (!response.data?.token || !response.data?.refreshToken) {
          throw new Error('Login failed: missing token in response.');
        }
        await storeTokens(response.data);
      } else {
        throw new Error(`Login failed (${response.status}).`);
      }
      console.log('Axios Error', response.status);
      return false;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorMessage =
          status === 401 || status === 403 || status === 422
            ? 'Invalid ID or password. Please try again.'
            : status === 429
              ? 'Too many attempts. Please wait and try again.'
              : status && status >= 500
                ? 'Server error. Please try again later.'
                : error.response?.statusText ||
                  (error.request && !error.response
                    ? 'Network error. Please check your connection.'
                    : error.message);
        throw new Error(errorMessage ?? error.message);
      }
      throw new Error('Login failed', { cause: error as Error });
    }
  };

  const logout = useCallback(async () => {
    localStorage.removeItem('exp');
    localStorage.removeItem('position');

    if (!accessToken) return;

    try {
      await signOut({ accessToken });
      if (isElectron) {
        await window.electron.ipcRenderer.removeRefreshToken();
      }
      resetAuthState();
      window.location.reload();
    } catch (error) {
      resetAuthState();
      window.location.reload();
      throw new Error('Failed to logout', { cause: error as Error });
    }
  }, [accessToken, isElectron, resetAuthState]);

  const refreshTokens = useCallback(async () => {
    try {
      let retrievedRefreshToken: string | null = null;

      if (isElectron) {
        retrievedRefreshToken =
          await window.electron.ipcRenderer.retrieveRefreshToken();

        if (!retrievedRefreshToken) {
          throw Error('no refreshtoken');
        }
      } else {
        retrievedRefreshToken = refreshToken;
        if (!retrievedRefreshToken) {
          throw Error('no refreshtoken');
        }
      }
      const response = await refreshAuth({
        refreshToken: retrievedRefreshToken,
      });

      if (response.status === 200 || response.status === 201) {
        await storeTokens(response.data);

        if (window.location.hash === '') {
          window.location.href = `/#${PATH.BROWSER}`;
        }
      } else {
        console.log('Axios Error', response.status);
        await logout();
      }
    } catch (error) {
      console.error('Failed to refresh tokens', error);
      await logout();
    }
  }, [isElectron, logout, storeTokens]);

  const { mutate: updatePassword } = useMutation({
    mutationFn: ({ body }: { body: UpdateMeReqBody }) =>
      patchPassword({
        accessToken,
        body,
      }),
    onSuccess: () => logout(),
    retry: false,
  });

  return {
    accessToken,
    tokenExpires,
    login,
    logout,
    updatePassword,
    refreshTokens,
  };
};

export default useAuth;
